import { enhanceStandardConsoleLogging } from '../../Util/Console/enhancer';
import { IConnectMessage } from './IConnectMessage';
import * as util from 'util';

const MAX_ERRORS_BUFFER_COUNT = 50;

export default class Connect {
	public static MESSAGE_PREFIX: string = 'connect';

	private connectedBaseUrl: string | null = null;
	private errorsMap: Record<string, Error> = {};

	/** @internal */
	constructor(
		public readonly window: Window,
		private messagePrefix: string,
	) {
		this.polyfillErrorWithStack();
		this.polyfillErrorEvent();
		this.interceptConsoleLogging();
		this.bindErrorHandling();
	}

	/** @internal */
	public handleMessageData(data: IConnectMessage) {
		switch (data.type) {
			case this.getMessage('update'):
				this.updateConnectedBaseUrl(data.connectedBaseUrl);
				break;
			default:
		}
	}

	private updateConnectedBaseUrl(connectedBaseUrl: string | null) {
		this.connectedBaseUrl = connectedBaseUrl;
	}

	private interceptConsoleLogging() {
		enhanceStandardConsoleLogging((type, ...args) => {
			if (this.connectedBaseUrl) {
				const serializedArgs = args.map((message) => util.inspect(message, true, 5, false));
				void fetch(`${this.connectedBaseUrl}/log`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						type,
						args: serializedArgs,
					}),
				});
			}
		});
	}

	private bindErrorHandling() {
		this.window.addEventListener('error', (event) => {
			if (this.connectedBaseUrl) {
				void fetch(`${this.connectedBaseUrl}/error`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						message: event.message,
						filename: event.filename,
						lineno: event.lineno,
						colno: event.colno,
						error: util.inspect(event.error, true, 5, false),
					}),
				});
			}
		});
	}

	private polyfillErrorWithStack() {
		const testError = new Error('test');
		if (testError.stack === undefined) {
			// Polyfill for Error constructor with stack for older browsers
			const ErrorOrig = Error;
			const pushErrorFn = this.pushError.bind(this); // Bind the method to avoid this alias
			type ErrorWithStack = Error & { stack: string; new (message: string): ErrorWithStack };
			function ErrorWithStackImpl(this: undefined | ErrorWithStack, message: string) {
				if (!(this instanceof ErrorWithStack)) {
					return new ErrorWithStack(message);
				}
				this.name = 'Error';
				let stack: string | undefined;
				try {
					// This is the only way to get a stack in old browsers
					throw new ErrorOrig();
				} catch (error) {
					stack = (error as Error).stack;
				}
				this.constructor = ErrorWithStack;
				this.message = message;
				this.stack = stack!;
				pushErrorFn(this);
			}
			const ErrorWithStack = ErrorWithStackImpl as unknown as ErrorWithStack;
			ErrorWithStack.prototype = ErrorOrig.prototype;
			(this.window as any).Error = ErrorWithStack;
		}
	}

	private polyfillErrorEvent() {
		// Polyfill ErrorEvent.error of addEventListener('error') for older browsers
		this.window.addEventListener('error', (event) => {
			if (event.error === undefined) {
				// Not standardized. ErrorEvent.message can be prefixed with 'Error: ' or 'Uncaught Error: ' based on version of chrome
				const errorMessage = Object.keys(this.errorsMap).find(function (key) {
					return event.message.indexOf(key) !== -1;
				});
				if (errorMessage) {
					const error = this.errorsMap[errorMessage];
					delete this.errorsMap[errorMessage];
					(event as { error: unknown }).error = error;
					console.warn('Error was polyfilled with not exact algorithm and may not be accurate', error);
				}
			}
		});
	}

	private pushError(error: Error) {
		this.errorsMap[error.message] = error;
		const keys = Object.keys(this.errorsMap);
		if (keys.length > MAX_ERRORS_BUFFER_COUNT) {
			// Remove the oldest error
			const oldestKey = keys[0];
			delete this.errorsMap[oldestKey];
		}
	}

	private getMessage(name: string) {
		return this.messagePrefix + '.' + Connect.MESSAGE_PREFIX + '.' + name;
	}
}
